import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { KpiCard } from '../../components/ui/KpiCard';
import { DataTable } from '../../components/ui/DataTable';
import { FilterTabs } from '../../components/ui/FilterTabs';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button, BetaTag } from '../../components/ui/Button';
import { CriticalAttentionPanel } from '../../components/dashboard/CriticalAttentionPanel';
import { DashboardTableRailLayout } from '../../components/dashboard/DashboardTableRailLayout';
import { OriginatorQuickActions } from '../../components/dashboard/OriginatorQuickActions';
import { DealsTab, LoansTab } from './PortfolioExplorerPage';
import {
  IconPackage,
  IconUpload,
  IconBuilding,
  IconAlert,
} from '../../components/ui/icons';
import { FEATURES } from '../../config/features';
import {
  getDashboardAlerts,
  getDashboardSummary,
  listBatches,
} from '../../api/originatorApi';
import { originatorNextAction, originatorPackagePath } from './originatorPackageLabels';

const TABLE_VIEWS = [
  { value: 'batch', label: 'Batch' },
  { value: 'deals', label: 'Deal / facility / SPV' },
  { value: 'loans', label: 'Loan' },
];

function tableHeading(view) {
  if (view === 'deals') return 'Deals / facilities';
  if (view === 'loans') return 'Loans';
  return 'Recent batches';
}

function tableViewAllHref(view) {
  if (view === 'deals') return '/loans?tab=deals';
  if (view === 'loans') return '/loans?tab=loans';
  return '/packages';
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isSameUtcDay(value, now) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return (
    date.getUTCFullYear() === now.getUTCFullYear() &&
    date.getUTCMonth() === now.getUTCMonth() &&
    date.getUTCDate() === now.getUTCDate()
  );
}

/** Custody accepted. The summary field `in_flight_packages` is draft/submitted/classified only. */
export function countInVault(rows) {
  return (rows || []).filter((row) => row.custody_status === 'ACCEPTED').length;
}

/**
 * Packages that left draft on the current UTC day.
 * The summary field only counts rows still sitting in SUBMITTED, so a package
 * classified or certified the same day it was submitted disappears from that count.
 */
export function countSubmittedToday(rows, now = new Date()) {
  return (rows || []).filter(
    (row) =>
      row.package_status &&
      row.package_status !== 'DRAFT' &&
      isSameUtcDay(row.created_at, now)
  ).length;
}

async function listAllBatches() {
  const pageSize = 100;
  const items = [];
  let total = 0;

  for (let page = 1; page <= 20; page += 1) {
    const response = await listBatches({ page, pageSize });
    const batch = response.items || [];
    total = response.meta?.total ?? items.length + batch.length;
    items.push(...batch);
    if (batch.length < pageSize || items.length >= total) break;
  }

  return items;
}

export function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableView, setTableView] = useState('batch');

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      setLoading(true);
      setError('');
      try {
        const [summaryResponse, alertResponse, packageRows] = await Promise.all([
          getDashboardSummary(),
          getDashboardAlerts(),
          listAllBatches(),
        ]);

        if (cancelled) return;
        setSummary(summaryResponse);
        setAlerts(alertResponse.items || []);
        setAllBatches(packageRows);
        setBatches(packageRows.slice(0, 10));
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load dashboard data.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, []);

  const kpis = useMemo(
    () => [
      {
        label: 'In vault',
        value: countInVault(allBatches),
        subline: 'Custody accepted by the custodian',
        icon: IconPackage,
        accent: true,
      },
      {
        label: 'Submitted today',
        value: countSubmittedToday(allBatches),
        subline: 'Submitted today (UTC), including packages already classified or certified',
        icon: IconUpload,
      },
      {
        label: 'Pending classification',
        value: summary?.pending_classification ?? 0,
        subline: 'Submitted and not yet classified',
        icon: IconBuilding,
      },
      {
        label: 'Open exceptions',
        value: summary?.open_exceptions ?? 0,
        subline: 'Open and in-cure exceptions',
        icon: IconAlert,
      },
    ],
    [allBatches, summary]
  );

  const batchColumns = useMemo(() => {
    const cols = [
      {
        key: 'batch',
        header: 'Batch',
        sortValue: (row) => row.reference_number || row.id,
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {row.reference_number || row.id}
            </span>
            <span className="block text-[11px] text-eorig-muted">
              {formatDateTime(row.created_at)}
              {FEATURES.sourceChannel && row.source_channel
                ? ` · ${row.source_channel}`
                : ''}
            </span>
          </div>
        ),
      },
      {
        key: 'program_id',
        header: 'Program',
        render: (row) =>
          row.program_id ? (
            <span className="inline-flex max-w-[160px] truncate rounded-md bg-eorig-surface px-2 py-1 font-mono text-[11px] text-eorig-muted">
              {row.program_id}
            </span>
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'package_status',
        header: 'Status',
        render: (row) => <StatusPill value={row.package_status} />,
      },
      {
        key: 'next_action',
        header: 'Next action',
        sortValue: (row) => originatorNextAction(row).label,
        render: (row) => {
          const nextAction = originatorNextAction(row);
          return (
            <StatusPill
              value={nextAction.label}
              tone={nextAction.tone}
              label={nextAction.label}
            />
          );
        },
      },
      {
        key: 'upload_status',
        header: 'Upload',
        render: (row) => (
          <span className="text-eorig-muted">{row.upload_status || '—'}</span>
        ),
      },
    ];
    return cols;
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BetaTag />
          <p className="text-[13px] text-eorig-muted">
            Your work vs custodian work, plus vault, pledge, and servicing
            visibility.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Link to="/packages">
            <Button variant="ghost">View packages</Button>
          </Link>
          <Link to="/submission/new">
            <Button variant="gradient">Upload batch</Button>
          </Link>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            subline={kpi.subline}
            icon={kpi.icon}
            accent={kpi.accent}
            loading={loading}
            trend={FEATURES.kpiTrends ? kpi.trend : undefined}
          />
        ))}
        {FEATURES.aiReview ? (
          <KpiCard
            label="Items in AI review"
            value={summary?.ai_review_count ?? 0}
            subline="Awaiting IDA classification review"
            loading={loading}
          />
        ) : null}
      </div>

      <DashboardTableRailLayout
        table={
          <>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-eorig-ink">
                {tableHeading(tableView)}
              </h2>
              <Link
                to={tableViewAllHref(tableView)}
                className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
              >
                View all
              </Link>
            </div>
            <div className="mb-3">
              <FilterTabs
                tabs={TABLE_VIEWS}
                value={tableView}
                ariaLabel="Filter dashboard table by batch, deal, or loan"
                onChange={setTableView}
              />
            </div>
            {tableView === 'batch' ? (
              <DataTable
                columns={batchColumns}
                rows={batches}
                rowKey={(row) => row.id}
                rowHref={(row) => originatorPackagePath(row.id)}
                loading={loading}
                emptyMessage="No batches yet."
                minWidth={640}
              />
            ) : null}
            {tableView === 'deals' ? <DealsTab /> : null}
            {tableView === 'loans' ? <LoansTab /> : null}
          </>
        }
        rail={({ maxHeight }) => (
          <CriticalAttentionPanel
            alerts={alerts}
            loading={loading}
            totalCount={summary?.open_exceptions}
            maxHeightPx={maxHeight}
          />
        )}
      />

      <OriginatorQuickActions />
    </div>
  );
}
